/* THE SECOND PANEL OF A CORNER SCREEN.

   A corner install is two flat panels meeting at a shared vertical edge. The
   first panel is solved exactly as a single screen always has been
   (core/calibrate.js) — four marks, four unknowns, one homography. This module
   answers the question that is left: given that camera, where does the second
   panel sit, and do its two outer marks agree with a 90° corner at all?

   The corner angle is ASSUMED to be 90°, decided deliberately. Real installs
   run 90° ± a degree or two, and solving for the angle as well would spend the
   extra marks on absorbing that error instead of reporting it — see the note on
   the residual below, which is the whole reason this is worth doing this way.

   Everything is in the PLANE FRAME that core/calibrate.js works in:
   X right, Y DOWN, Z INTO the screen, origin at the first panel's centre. So
   the first panel is z = 0, x ∈ [−W/2, W/2], y ∈ [−H/2, H/2], and the viewer
   sits at negative Z. The hinge — the shared edge — is the vertical line
   x = +W/2, z = 0, and the second panel lies in the plane x = W/2.

   No DOM, no three, no S, arguments only: the same contract core/video-time.js
   and core/crop-map.js keep, and for the same reason. It is what lets a
   synthetic rig with a known camera and a known 90° corner be projected,
   fed back in, and checked to a tolerance — which is the only way to be sure
   the frame conventions above are right rather than merely plausible. */

const EPS_DIR = 1e-9;

/* n1/n2/n3 are the ROWS of camera→plane, equivalently the COLUMNS of
   plane→camera. Which way round that falls is not something to reason out and
   hope: both pairings produce finite, plausible-looking directions, and the
   wrong one silently lands the second panel somewhere behind the viewer. It was
   measured against a synthetic rig with a known camera — see tools/verify.mjs's
   corner section, which checks the recovered point to 1e-12 m. */
const dot3 = (a, v) => a[0]*v[0] + a[1]*v[1] + a[2]*v[2];

/** Plane-frame direction of the ray through a pixel. */
function rayDir(pose, px, py) {
  const v = [(px - pose.cx) / pose.f, (py - pose.cy) / pose.f, 1];
  return [dot3(pose.n1, v), dot3(pose.n2, v), dot3(pose.n3, v)];
}

/** Plane-frame point → pixel. The inverse of rayDir, and the residual's basis. */
export function projectPoint(pose, P) {
  const { n1, n2, n3, t, cx, cy, f } = pose;
  const x = n1[0]*P[0] + n2[0]*P[1] + n3[0]*P[2] + t[0];
  const y = n1[1]*P[0] + n2[1]*P[1] + n3[1]*P[2] + t[1];
  const z = n1[2]*P[0] + n2[2]*P[1] + n3[2]*P[2] + t[2];
  if (!(Math.abs(z) > 1e-12)) return null;
  return [cx + f*x/z, cy + f*y/z];
}

/**
 * Where the second panel sits, from the two marks on its outer edge.
 *
 * @param {{w:number,h:number}} photo   pixel dimensions of the reference photo
 * @param {object} pose                 from solveCalibration's `pose`
 * @param {{w:number,h:number}} screen  the FIRST panel, metres
 * @param {number} cornerW              the second panel's width as typed, metres
 * @param {{tr:{x,y}, br:{x,y}}} outer   its two far marks, normalised 0..1
 * @returns {object} measurements, or {error}
 */
export function solveCornerPanel({ photo, pose, screen, cornerW, outer }) {
  if (!pose || !outer || !outer.tr || !outer.br) return { error: 'The second panel’s corners aren’t marked yet.' };
  const W = screen.w, H = screen.h;
  if (!(W > 0 && H > 0)) return { error: 'Enter the screen’s real width and height in metres first.' };

  const hinge = W / 2;
  const hit = (mark) => {
    const d = rayDir(pose, mark.x * photo.w, mark.y * photo.h);
    /* A ray running parallel to the second panel never meets it. Geometrically
       this is the viewer standing edge-on to that panel, which is also the case
       where its picture would be a sliver — worth its own sentence rather than
       an Infinity propagating into the numbers below. */
    if (Math.abs(d[0]) < EPS_DIR) return null;
    const lam = (hinge - pose.C[0]) / d[0];
    if (!(lam > 0)) return null;                 // the panel is behind the camera
    return [pose.C[0] + lam*d[0], pose.C[1] + lam*d[1], pose.C[2] + lam*d[2]];
  };

  const Ptop = hit(outer.tr), Pbot = hit(outer.br);
  if (!Ptop || !Pbot)
    return { error: 'Those marks don’t meet a panel at right angles to the first one. Check they are on the far edge of the second panel, not on the first.' };

  /* Convex and concave are the SAME PLANE — they differ only in which half of
     it the panel occupies — so this is read off the sign of the depth, not
     searched for by trying both and comparing. Plane-frame +Z runs away from
     the viewer, so a panel folding away from them (the outside corner of a
     building) lands at positive z. */
  const uTop = Ptop[2], uBot = Pbot[2];
  if (Math.sign(uTop) !== Math.sign(uBot))
    return { error: 'The two outer marks fall on opposite sides of the corner, so they can’t describe one flat panel. Re-mark them on the same face.' };

  const convex = (uTop + uBot) > 0;
  const wMeasured = (Math.abs(uTop) + Math.abs(uBot)) / 2;

  /* Three numbers, each pointing at a different fixable thing, because "it is
     a bit off" is not actionable and these are.

     They are meaningful precisely BECAUSE the pose came from the first panel
     alone: these two marks took no part in solving it, so their disagreement is
     a genuine independent test of the two assumptions this rests on — that the
     corner is square and that the panels are the same height. A joint fit over
     all six marks would bury that error in the camera and report a residual
     near zero whether the assumptions held or not. */
  const heightErrM = (Math.abs(Ptop[1] + H/2) + Math.abs(Pbot[1] - H/2)) / 2;
  const tiltM = Math.abs(Math.abs(uTop) - Math.abs(uBot));

  /* And one scalar for the chip: where the two marks WOULD be if the corner were
     exactly square and the typed width exactly right. Reported in pixels for the
     threshold and in metres at the outer edge for the human — a degree of error
     at three metres is about five centimetres out there, and centimetres are the
     only unit in which someone can judge whether it matters before cutting. */
  const s = convex ? 1 : -1, w = cornerW > 0 ? cornerW : wMeasured;
  const idealTop = [hinge, -H/2, s*w], idealBot = [hinge, H/2, s*w];
  const qTop = projectPoint(pose, idealTop), qBot = projectPoint(pose, idealBot);
  let reprojPx = null;
  if (qTop && qBot) {
    const mTop = [outer.tr.x*photo.w, outer.tr.y*photo.h];
    const mBot = [outer.br.x*photo.w, outer.br.y*photo.h];
    reprojPx = Math.max(Math.hypot(qTop[0]-mTop[0], qTop[1]-mTop[1]),
                        Math.hypot(qBot[0]-mBot[0], qBot[1]-mBot[1]));
  }

  /* How square-on the second panel is seen: 0° is face-on, 90° is edge-on.
     This is not decoration — it is the confidence interval on wMeasured.
     Measured against a synthetic rig with a deliberate 2° corner error, the
     recovered width degrades sharply as the panel turns away:

         seen 52° off square →  2.09 m against a true 2.20   (−5%)
         seen 71°            →  1.96                         (−11%)
         seen 80°            →  1.75                         (−21%)
         seen 87°            →  0.44                         (−80%)

     which is inherent: a panel viewed edge-on carries almost no depth
     information, so a small error in the corner angle moves the intersection a
     long way along it. reprojPx, by contrast, held at ~20 px throughout — so
     THAT is the number to warn on, and wMeasured is the number to stop
     offering once this passes ~75°. */
  const toCam = [pose.C[0] - hinge, pose.C[1], pose.C[2] - (convex ? 1 : -1)*wMeasured/2];
  const tl = Math.hypot(toCam[0], toCam[1], toCam[2]) || 1;
  const obliqDeg = Math.acos(Math.min(1, Math.abs(toCam[0]) / tl)) * 180 / Math.PI;

  return {
    convex, wMeasured, tiltM, heightErrM, reprojPx, obliqDeg,
    widthTrustworthy: obliqDeg < 75,
    widthErrM: Math.abs(wMeasured - w)
  };
}
